import React, { forwardRef, KeyboardEvent, MouseEvent } from 'react'
import clsx from 'clsx'
import { SideNav as SideNavStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { CommonProps, PolymorphicPropsWithRef, PolymorphicRef } from '../helperTypes'

const useStyles = createUseStyles(SideNavStyles, 'sidenav')

type Props = {
  /** Disabled state */
  disabled?: boolean
  /** optional href url */
  href?: string
  /** handler dispatched when the item is selected */
  onSelected?: (href?: string) => void
  /** Selected state */
  selected?: boolean
}

export type SideNavItemProps<C extends React.ElementType> = PolymorphicPropsWithRef<C, Props & CommonProps>

// type annotation for strong typing of polymorphic ref
type PolymorphicSideNavItem = <C extends React.ElementType = 'li'>(
  props: SideNavItemProps<C>,
) => React.ReactElement | null

export const SideNavItem: PolymorphicSideNavItem = forwardRef(
  <C extends React.ElementType>(props: SideNavItemProps<C>, ref?: PolymorphicRef<C>) => {
    const {
      children,
      className,
      component,
      disabled = false,
      href,
      onSelected,
      selected = false,
      style,
      ...rest
    } = props
    const classes = useStyles()
    const ComponentProp = component || 'a'

    const dispatchSelect = (evt: KeyboardEvent | MouseEvent, stopPropagation: boolean = false) => {
      if (stopPropagation) {
        evt.stopPropagation()
        return
      }
      if (onSelected) onSelected(href)
    }

    return (
      <li
        ref={ref}
        className={clsx(className, classes.item, disabled && classes.item_disabled, selected && classes.item_selected)}
        style={style}
      >
        <ComponentProp
          className={classes.itemLink}
          href={href}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(evt: KeyboardEvent) => {
            if (evt.key === ' ' || evt.key === 'Enter') dispatchSelect(evt, disabled)
          }}
          onClick={(evt: MouseEvent) => dispatchSelect(evt, disabled)}
          {...rest}
        >
          {children}
        </ComponentProp>
      </li>
    )
  },
)

export default SideNavItem
